import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Flame, Calculator, AlertTriangle, Sun, Check } from "lucide-react";
import { runOrQueue } from "@/lib/offlineQueue";
import { PlausibilityBanner } from "@/components/PlausibilityBanner";
import PvRecommendationCard from "@/components/PvRecommendationCard";

const inputCls = "h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-[15px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none";

// Brennstoff-Faktoren (kWh je Einheit). Iter89a — Hartholz/Weichholz.
// Iter89z (2026-02-25) — Flüssiggas (LPG) ergänzt: 6,56 kWh/Liter (Propan Hi).
const FUEL_KWH_PER_UNIT = {
  gas: 10.0,       // pro m³ Erdgas H
  oil: 10.0,       // pro Liter Heizöl
  lpg: 6.56,       // pro Liter Flüssiggas (Propan Heizwert)
  pellets: 4.8,    // pro kg Pellets
};
const WOOD_KWH_PER_SRM = { hartholz: 2100, weichholz: 1500, gemischt: 1800 };
const WOOD_TYPE_LABEL = { hartholz: "Hartholz (Buche/Eiche)", weichholz: "Weichholz (Fichte/Kiefer)", gemischt: "Gemischt" };

// Plausibilitäts-Range pro Sanierungsstand: erwartete kWh/m² Wohnfläche pro Jahr.
const KWH_PER_SQM_RANGE = {
  unsaniert: [180, 280],
  teilweise: [120, 200],
  saniert: [70, 130],
  kfw: [30, 80],
};

function fmtNum(n) { return Number(n || 0).toLocaleString("de-DE"); }

// Konvertierter Verbrauch je Zeile (kompakter Hint direkt unter dem Input).
function ConsumptionHint({ unit, amount, kwhPerUnit }) {
  if (!amount || Number(amount) <= 0) return null;
  const kwh = Math.round(Number(amount) * kwhPerUnit);
  return (
    <div className="text-[11.5px] text-zinc-500 mt-1 font-mono">
      ≈ <b className="text-zinc-200">{fmtNum(kwh)} kWh</b>
      <span> ({amount} {unit} × {kwhPerUnit.toString().replace(".", ",")})</span>
    </div>
  );
}

const ENERGY_SOURCES = ["gas","lpg","oil","pellets","wood","coal","electric_night","electricity","district"];
const ENERGY_LABEL = {
  gas: "Gas", lpg: "Flüssiggas", oil: "Öl", coal: "Kohle", electric_night: "Nachtspeicher",
  pellets: "Pellets", electricity: "Strom", wood: "Holz", district: "Fernwärme"
};
// Zusätzliche Quellen mit eigenem Feld (Kohle in kg, Rest direkt in kWh) – spiegelt calc_heat_load.
const EXTRA_INPUTS = [
  { src: "coal", key: "annual_consumption_coal_kg", label: "Kohle kg / Jahr", unit: "kg", kwh: 5.4 },
  { src: "electric_night", key: "annual_consumption_night_storage_kwh", label: "Nachtspeicher kWh / Jahr", unit: "kWh", kwh: 1 },
  { src: "electricity", key: "annual_consumption_electricity_kwh", label: "Strom (Heizung) kWh / Jahr", unit: "kWh", kwh: 1 },
  { src: "district", key: "annual_consumption_district_kwh", label: "Fernwärme kWh / Jahr", unit: "kWh", kwh: 1 },
];
const EXTRA_KEYS = EXTRA_INPUTS.map((x) => x.key);
// BEG-Alterssperre (Klima-Bonus 16 % nur wenn Anlage ≥ 20 J.)
// Iter89z — Flüssiggas zählt BEG-rechtlich wie Gas (Beschleunigungsbonus bei ≥ 20 J.).
const BEG_AGE_RELEVANT = new Set(["gas", "lpg", "oil", "coal", "electric_night"]);
const RENOVATION = [
  { v: "unsaniert", label: "Unsaniert" },
  { v: "teilweise", label: "Teilweise saniert" },
  { v: "saniert", label: "Vollsaniert" },
  { v: "kfw", label: "KfW / energetisch hochwertig" },
];

export default function ConsumptionSection({ project, consumption, onUpdate }) {
  const [form, setForm] = useState(() => {
    const initial = {
      current_energy_source: "gas",
      current_energy_sources: [],
      annual_consumption_kwh: "",
      annual_consumption_gas_m3: "",
      annual_consumption_lpg_liters: "",
      annual_consumption_oil_liters: "",
      annual_consumption_pellets_kg: "",
      annual_consumption_wood_srm: "",
      ...Object.fromEntries(EXTRA_KEYS.map((k) => [k, ""])),
      wood_type: "gemischt",
      has_solar_thermal: false,
      solar_thermal_yield_kwh: "",
      warm_water_persons: 3,
      renovation_status: "teilweise",
      heated_area: "",
      building_year: "",
      existing_heating_year: "",
      is_neubau: false,
      notes: "",
      ...(consumption || {}),
    };
    // Legacy-Migration: Falls Multi-Array leer, aber Single-Source gesetzt,
    // Multi-Array aus Single-Source vorbelegen (rückwärts-kompatibel).
    if (!Array.isArray(initial.current_energy_sources) || initial.current_energy_sources.length === 0) {
      initial.current_energy_sources = initial.current_energy_source ? [initial.current_energy_source] : ["gas"];
    }
    return initial;
  });
  const [loading, setLoading] = useState(false);
  const resultRef = useRef(null);
  const [justCalculated, setJustCalculated] = useState(false);

  useEffect(() => {
    if (justCalculated && resultRef.current) {
      const t = setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
      setJustCalculated(false);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [justCalculated]);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Multi-Source-Toggle. Erste ausgewählte Quelle wird als "primary" gemerkt
  // (relevant für BEG-Alter, wenn genau eine BEG-Quelle aktiv).
  const toggleSource = (src) => setForm(prev => {
    const list = Array.isArray(prev.current_energy_sources) ? [...prev.current_energy_sources] : [];
    const idx = list.indexOf(src);
    if (idx >= 0) list.splice(idx, 1);
    else list.push(src);
    return {
      ...prev,
      current_energy_sources: list,
      current_energy_source: list[0] || src,
    };
  });

  const sources = Array.isArray(form.current_energy_sources) ? form.current_energy_sources : [];
  const hasSource = (s) => sources.includes(s);

  // Live-Summe aller ausgewählten Verbräuche (nur Preview, Backend rechnet echt).
  const liveKwh = (() => {
    let total = 0;
    if (hasSource("gas")) total += Number(form.annual_consumption_gas_m3 || 0) * 10;
    if (hasSource("lpg")) total += Number(form.annual_consumption_lpg_liters || 0) * FUEL_KWH_PER_UNIT.lpg;
    if (hasSource("oil")) total += Number(form.annual_consumption_oil_liters || 0) * 10;
    if (hasSource("pellets")) total += Number(form.annual_consumption_pellets_kg || 0) * 4.8;
    if (hasSource("wood")) total += Number(form.annual_consumption_wood_srm || 0) * (WOOD_KWH_PER_SRM[form.wood_type] || 1800);
    for (const x of EXTRA_INPUTS) if (hasSource(x.src)) total += Number(form[x.key] || 0) * x.kwh;
    // „Direkt in kWh“ wird immer zusätzlich addiert (wie im Backend)
    total += Number(form.annual_consumption_kwh || 0);
    // Solar-Ertrag abziehen
    if (form.has_solar_thermal) {
      const yld = Number(form.solar_thermal_yield_kwh || 0) || (Math.max(1, Number(form.warm_water_persons || 0)) * 400);
      total = Math.max(0, total - yld);
    }
    return Math.round(total);
  })();

  const directKwh = Number(form.annual_consumption_kwh || 0);
  const fuelKwh = liveKwh - directKwh;
  const maybeDuplicate = directKwh > 0 && fuelKwh > 0 && Math.abs(directKwh - fuelKwh) / fuelKwh < 0.05;

  const save = async () => {
    setLoading(true);
    try {
      const toNumOrNull = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const numericKeys = [
        "annual_consumption_kwh", "annual_consumption_gas_m3",
        "annual_consumption_lpg_liters",
        "annual_consumption_oil_liters", "annual_consumption_pellets_kg",
        "annual_consumption_wood_srm", ...EXTRA_KEYS,
        "solar_thermal_yield_kwh",
        "warm_water_persons", "heated_area", "building_year",
        "existing_heating_year",
      ];
      const payload = {
        ...form,
        is_neubau: !!form.is_neubau,
        has_solar_thermal: !!form.has_solar_thermal,
      };
      // Nur Verbräuche der aktuell ausgewählten Quellen ans Backend senden —
      // sonst rechnet die Faustformel abgewählte Reste (z.B. Öl-Verbrauch,
      // der versehentlich stehen bleibt) mit hinein.
      if (!hasSource("gas")) payload.annual_consumption_gas_m3 = null;
      if (!hasSource("lpg")) payload.annual_consumption_lpg_liters = null;
      if (!hasSource("oil")) payload.annual_consumption_oil_liters = null;
      if (!hasSource("pellets")) payload.annual_consumption_pellets_kg = null;
      if (!hasSource("wood")) payload.annual_consumption_wood_srm = null;
      for (const x of EXTRA_INPUTS) if (!hasSource(x.src)) payload[x.key] = null;
      for (const k of numericKeys) {
        if (k in payload) payload[k] = toNumOrNull(payload[k]);
      }
      const r = await runOrQueue({
        method: "post",
        url: `/projects/${project.id}/energy-consumption`,
        data: payload,
        label: "Verbrauch & Heizlast",
      });
      if (r.offline) {
        toast.success("Offline gespeichert – Heizlast wird nach Sync berechnet");
      } else {
        setForm(prev => {
          const numericInputs = [
            "annual_consumption_kwh", "annual_consumption_gas_m3",
            "annual_consumption_lpg_liters",
            "annual_consumption_oil_liters", "annual_consumption_pellets_kg",
            "annual_consumption_wood_srm", ...EXTRA_KEYS,
            "warm_water_persons", "heated_area", "building_year",
            "existing_heating_year",
          ];
          const merged = { ...prev, ...r.data };
          for (const k of numericInputs) {
            if (prev[k] !== "" && prev[k] !== null && prev[k] !== undefined) {
              merged[k] = prev[k];
            }
          }
          return merged;
        });
        toast.success(`Heizlast: ${r.data.calculated_heat_load_kw} kW`);
        setJustCalculated(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setLoading(false);
    }
  };

  const primary = sources[0] || "gas";
  const showBegYear = sources.some((s) => BEG_AGE_RELEVANT.has(s));
  const begYearSourceLabel = ENERGY_LABEL[sources.find((s) => BEG_AGE_RELEVANT.has(s)) || primary] || primary;

  return (
    <div className="space-y-5">
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <Flame size={18} className="text-[#00E575]" /> Energieträger & Verbrauch
          {sources.length > 1 && (
            <span className="ml-auto text-[11px] font-mono uppercase tracking-widest text-[#00E575] bg-[#00E575]/12 border border-[#00E575]/30 px-2 py-0.5 rounded-full">
              Hybrid · {sources.length} Quellen
            </span>
          )}
        </h2>

        <div>
          <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">
            Aktuelle Energieträger <span className="text-zinc-500 font-medium normal-case tracking-normal">— Mehrfachauswahl möglich</span>
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {ENERGY_SOURCES.map((s) => {
              const on = hasSource(s);
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`consumption-source-${s}`}
                  onClick={() => toggleSource(s)}
                  className={`relative h-12 rounded-xl border font-medium text-sm transition active:scale-95 flex items-center justify-center gap-1 ${
                    on
                      ? "bg-[#00E575]/15 border-[#00E575] text-[#00E575]"
                      : "bg-[#09090B] border-zinc-700 text-zinc-300 hover:border-zinc-500"
                  }`}
                >
                  {on && <Check size={13} className="text-[#00E575]" strokeWidth={3} />}
                  {ENERGY_LABEL[s]}
                </button>
              );
            })}
          </div>
          {sources.length === 0 && (
            <div className="mt-2 text-[11.5px] text-amber-300 font-medium">
              Mindestens eine Energiequelle auswählen.
            </div>
          )}
        </div>

        {/* Verbrauchsfelder pro selektierter Quelle */}
        <div className="grid grid-cols-2 gap-3">
          {hasSource("gas") && (
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Gas m³ / Jahr</label>
              <input data-testid="consumption-gasm3" type="number" className={inputCls} value={form.annual_consumption_gas_m3 || ""} onChange={(e) => upd("annual_consumption_gas_m3", e.target.value)} />
              <ConsumptionHint unit="m³" amount={form.annual_consumption_gas_m3} kwhPerUnit={FUEL_KWH_PER_UNIT.gas} />
            </div>
          )}
          {hasSource("lpg") && (
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Flüssiggas Liter / Jahr</label>
              <input data-testid="consumption-lpg" type="number" className={inputCls} value={form.annual_consumption_lpg_liters || ""} onChange={(e) => upd("annual_consumption_lpg_liters", e.target.value)} />
              <ConsumptionHint unit="l" amount={form.annual_consumption_lpg_liters} kwhPerUnit={FUEL_KWH_PER_UNIT.lpg} />
            </div>
          )}
          {hasSource("oil") && (
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Öl Liter / Jahr</label>
              <input data-testid="consumption-oil" type="number" className={inputCls} value={form.annual_consumption_oil_liters || ""} onChange={(e) => upd("annual_consumption_oil_liters", e.target.value)} />
              <ConsumptionHint unit="l" amount={form.annual_consumption_oil_liters} kwhPerUnit={FUEL_KWH_PER_UNIT.oil} />
            </div>
          )}
          {hasSource("pellets") && (
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Pellets kg / Jahr</label>
              <input data-testid="consumption-pellets" type="number" className={inputCls} value={form.annual_consumption_pellets_kg || ""} onChange={(e) => upd("annual_consumption_pellets_kg", e.target.value)} />
              <ConsumptionHint unit="kg" amount={form.annual_consumption_pellets_kg} kwhPerUnit={FUEL_KWH_PER_UNIT.pellets} />
            </div>
          )}
          {hasSource("wood") && (
            <div className="col-span-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Scheitholz rm / Jahr</label>
                  <input data-testid="consumption-wood-srm" type="number" className={inputCls} value={form.annual_consumption_wood_srm || ""} onChange={(e) => upd("annual_consumption_wood_srm", e.target.value)} />
                  <ConsumptionHint unit="rm" amount={form.annual_consumption_wood_srm} kwhPerUnit={WOOD_KWH_PER_SRM[form.wood_type] || 1800} />
                </div>
                <div>
                  <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Härtegrad</label>
                  <select
                    data-testid="consumption-wood-type"
                    className={inputCls}
                    value={form.wood_type || "gemischt"}
                    onChange={(e) => upd("wood_type", e.target.value)}
                  >
                    {Object.entries(WOOD_TYPE_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  <div className="text-[11.5px] text-zinc-500 mt-1 font-mono">
                    {WOOD_KWH_PER_SRM[form.wood_type] || 1800} kWh / rm
                  </div>
                </div>
              </div>
            </div>
          )}

          {EXTRA_INPUTS.filter((x) => hasSource(x.src)).map((x) => (
            <div key={x.src} className="col-span-2 sm:col-span-1">
              <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">{x.label}</label>
              <input data-testid={`consumption-${x.src}`} type="number" className={inputCls} value={form[x.key] || ""} onChange={(e) => upd(x.key, e.target.value)} />
              {x.kwh !== 1 && <ConsumptionHint unit={x.unit} amount={form[x.key]} kwhPerUnit={x.kwh} />}
            </div>
          ))}

          {/* Solarthermie — als Ergänzung/Abzug */}
          <div className="col-span-2">
            <label className={`flex items-start gap-2 cursor-pointer rounded-xl border p-3 transition ${form.has_solar_thermal ? "bg-amber-500/8 border-amber-400/50" : "bg-[#09090B] border-zinc-700"}`}>
              <input
                type="checkbox"
                data-testid="consumption-solar-thermal"
                checked={!!form.has_solar_thermal}
                onChange={(e) => upd("has_solar_thermal", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-amber-400 bg-[#0F1418] text-amber-500 focus:ring-amber-500"
              />
              <div className="flex-1">
                <div className={`text-[13px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${form.has_solar_thermal ? "text-amber-200" : "text-zinc-200"}`}>
                  <Sun size={14} /> Solarthermie vorhanden
                </div>
                <div className="text-[11.5px] text-zinc-400 mt-0.5 leading-snug normal-case tracking-normal">
                  Ertrag wird vom Jahresverbrauch abgezogen. Standard-Faustwert: ca. 400 kWh/Person Warmwasser.
                </div>
                {form.has_solar_thermal && (
                  <div className="mt-2">
                    <label className="block text-[11px] font-semibold text-amber-100/80 mb-1 uppercase tracking-wider">Ertrag kWh / Jahr (optional)</label>
                    <input
                      type="number"
                      data-testid="consumption-solar-thermal-yield"
                      placeholder={`Standard: ${Math.max(1, Number(form.warm_water_persons || 0)) * 400} kWh (${Math.max(1, Number(form.warm_water_persons || 0))} Personen × 400)`}
                      className="h-10 w-full bg-[#09090B] border border-amber-400/40 rounded-lg px-3 text-[13px] text-zinc-50 outline-none focus:border-amber-300"
                      value={form.solar_thermal_yield_kwh || ""}
                      onChange={(e) => upd("solar_thermal_yield_kwh", e.target.value)}
                    />
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Live-Summe Vorschau bei ≥ 2 Quellen */}
          {(sources.length > 1 || Number(form.annual_consumption_kwh || 0) > 0 || (form.has_solar_thermal && sources.length >= 1)) && liveKwh > 0 && (
            <div className="col-span-2 rounded-xl bg-[#00E575]/6 border border-[#00E575]/25 px-4 py-3 flex items-center justify-between">
              <div className="text-[12px] font-bold uppercase tracking-widest text-[#00E575]">
                Summe (Vorschau)
              </div>
              <div className="font-mono text-lg font-bold text-[#00E575]">
                ≈ {fmtNum(liveKwh)} kWh / Jahr
              </div>
            </div>
          )}

          <div><label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Zusätzlich in kWh (optional)</label><input data-testid="consumption-kwh" type="number" className={inputCls} value={form.annual_consumption_kwh || ""} onChange={(e) => upd("annual_consumption_kwh", e.target.value)} /><div className="text-[11px] text-zinc-500 mt-1" data-testid="consumption-kwh-hint">Wird zu den Mengen addiert – z. B. Strom, Fernwärme oder ein Verbrauch, der schon in kWh vorliegt.</div>{maybeDuplicate && <div className="text-[11.5px] text-amber-300 mt-1 font-semibold" data-testid="consumption-duplicate-warning">Achtung: Dieser Wert entspricht etwa der Menge oben – wurde der Verbrauch doppelt erfasst? Dann dieses Feld leeren.</div>}</div>
          <div><label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Personen im Haushalt</label><input data-testid="consumption-persons" type="number" className={inputCls} value={form.warm_water_persons || ""} onChange={(e) => upd("warm_water_persons", e.target.value)} /></div>
          <div><label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Wohnfläche m²</label><input data-testid="consumption-area" type="number" className={inputCls} value={form.heated_area || ""} onChange={(e) => upd("heated_area", e.target.value)} /></div>
          <div><label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Baujahr Gebäude</label><input data-testid="consumption-buildingyear" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} className={inputCls} value={form.building_year || ""} onChange={(e) => upd("building_year", String(e.target.value).replace(/\D/g, "").slice(0, 4))} /></div>
          <div className="col-span-2">
            <label className="flex items-start gap-2 cursor-pointer rounded-xl bg-red-500/8 border border-red-500/30 p-3">
              <input
                type="checkbox"
                data-testid="consumption-is-neubau"
                checked={!!form.is_neubau}
                onChange={(e) => upd("is_neubau", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-red-400 bg-[#0F1418] text-red-500 focus:ring-red-500"
              />
              <div className="flex-1">
                <div className="text-[13px] font-bold text-red-200 uppercase tracking-wider">Neubau — keine KfW-458-Förderung</div>
                <div className="text-[11.5px] text-red-100/80 mt-0.5 leading-snug normal-case tracking-normal">
                  Wenn aktiviert, entfällt die gesamte Förderberechnung nach KfW 458. Das Programm gilt nur für den Heizungstausch in Bestandsgebäuden. Für Neubauten ggf. KfW 297/298 (BEG-KFN) prüfen.
                </div>
              </div>
            </label>
          </div>
          {showBegYear && (
            <div className="col-span-2">
              <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">
                Baujahr {begYearSourceLabel}-Anlage
                <span className="ml-2 text-[10.5px] text-zinc-400 font-medium normal-case tracking-normal">(für KfW-Klima-Bonus · Mindestalter 20 J.)</span>
              </label>
              <input
                data-testid="consumption-heating-year"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                placeholder="z. B. 2005"
                className={inputCls}
                value={form.existing_heating_year || ""}
                onChange={(e) => {
                  const digits = String(e.target.value).replace(/\D/g, "").slice(0, 4);
                  upd("existing_heating_year", digits);
                }}
              />
              {form.existing_heating_year && Number(form.existing_heating_year) > 1900 && (() => {
                const age = new Date().getFullYear() - Number(form.existing_heating_year);
                const eligible = age >= 20;
                return (
                  <div className={`text-[12px] mt-1.5 font-medium ${eligible ? "text-[#00E575]" : "text-amber-400"}`}>
                    Anlagenalter: <b>{age} Jahre</b>
                    {eligible
                      ? " · Klima-Bonus (16 %) förderfähig ✓"
                      : ` · Klima-Bonus gesperrt (< 20 Jahre) ⚠︎`}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div>
          <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">Sanierungsstand</label>
          <div className="grid grid-cols-2 gap-2">
            {RENOVATION.map((r) => (
              <button key={r.v} data-testid={`consumption-reno-${r.v}`} onClick={() => upd("renovation_status", r.v)}
                className={`h-12 rounded-xl border font-medium text-sm transition active:scale-95 ${
                  form.renovation_status === r.v
                    ? "bg-[#00E575]/15 border-[#00E575] text-[#00E575]"
                    : "bg-[#09090B] border-zinc-700 text-zinc-300"
                }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {form.plausibility && <PlausibilityBanner plausibility={form.plausibility} />}

      <button
        data-testid="consumption-save-btn"
        onClick={save}
        disabled={loading}
        className="h-14 w-full bg-[#00E575] text-[#09090B] font-semibold text-lg rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition flex items-center justify-center"
      >
        {loading ? <Loader2 className="animate-spin" /> : <><Calculator size={20} className="mr-2" /> Heizlast berechnen & speichern</>}
      </button>

      {form.calculated_heat_load_kw != null && (
        <div
          ref={resultRef}
          data-testid="heatload-result-card"
          className="bg-gradient-to-br from-[#00E575]/15 to-[#00E575]/5 border border-[#00E575]/30 rounded-2xl p-5 scroll-mt-24"
        >
          <div className="flex items-center gap-2 mb-3"><Calculator className="text-[#00E575]" size={20} /><h3 className="font-display text-lg font-semibold">Heizlast-Grobschätzung</h3></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#09090B]/60 rounded-xl p-4"><div className="text-xs text-zinc-400 uppercase tracking-wider">Jahresverbrauch</div><div className="font-display text-xl font-bold mt-1">{form.normalized_annual_kwh ?? form.calculated_heating_energy_kwh} kWh</div><div className="text-[10px] text-zinc-500 mt-1">Basis der Heizlast</div></div>
            <div className="bg-[#09090B]/60 rounded-xl p-4"><div className="text-xs text-zinc-400 uppercase tracking-wider">Warmwasser (Info)</div><div className="font-display text-xl font-bold mt-1">{form.calculated_warm_water_kwh} kWh</div><div className="text-[10px] text-zinc-500 mt-1">bereits in VBH enthalten</div></div>
            <div className="bg-[#09090B]/60 rounded-xl p-4"><div className="text-xs text-zinc-400 uppercase tracking-wider">Vollbenutzung</div><div className="font-display text-xl font-bold mt-1">{form.full_load_hours} h</div></div>
            <div className="bg-[#00E575] text-[#09090B] rounded-xl p-4"><div className="text-xs uppercase tracking-wider font-semibold">Heizlast</div><div className="font-display text-3xl font-bold mt-1" data-testid="heatload-result">{form.calculated_heat_load_kw} kW</div></div>
          </div>
          <div className="mt-4 flex items-start gap-2 text-xs text-zinc-400 bg-zinc-800/40 border border-zinc-700/60 rounded-lg p-3">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-zinc-500" />
            <span>Faustformel <b>Jahresverbrauch ÷ Vollbenutzungsstunden</b>. Überschlägige Orientierung — die DIN EN 12831 ist Bestandteil der Raum-für-Raum-Heizlastberechnung.</span>
          </div>
        </div>
      )}

      {consumption?.annual_consumption_kwh > 0 && (
        <PvRecommendationCard projectId={project.id} />
      )}
    </div>
  );
}
